import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Upload, Trash2, Loader2, Image, Sparkles, AlertCircle, Check } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

interface LoanOfficerAd {
  id: string;
  image_url: string;
  is_active: boolean;
  created_at: string;
}

interface LoanOfficerAdManagerProps {
  loanOfficerId: string;
  officerName: string;
  nmls: string;
  photoUrl: string | null;
  logoUrl: string | null;
  adImageUrl: string | null;
  onAdImageChange: (url: string | null) => void;
}

export function LoanOfficerAdManager({
  loanOfficerId,
  officerName,
  nmls,
  photoUrl,
  logoUrl,
  adImageUrl,
  onAdImageChange,
}: LoanOfficerAdManagerProps) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [ads, setAds] = useState<LoanOfficerAd[]>([]);
  const [loadingAds, setLoadingAds] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasRequiredInfo = officerName.trim() !== '' && nmls.trim() !== '';

  // Fetch existing ads for this loan officer
  const fetchAds = async () => {
    setLoadingAds(true);
    const { data, error } = await supabase
      .from('loan_officer_ads')
      .select('*')
      .eq('loan_officer_id', loanOfficerId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching ads:', error);
      setAds([]);
    } else if (data && data.length > 0) {
      setAds(data);
    } else if (adImageUrl) {
      // No ads in new table, but we have a legacy ad_image_url - migrate it
      const { data: insertedAd, error: insertError } = await supabase
        .from('loan_officer_ads')
        .insert({
          loan_officer_id: loanOfficerId,
          image_url: adImageUrl,
          is_active: true,
        })
        .select()
        .single();

      if (!insertError && insertedAd) {
        setAds([insertedAd]);
      } else {
        console.error('Error migrating legacy ad:', insertError);
        setAds([]);
      }
    } else {
      setAds([]);
    }
    setLoadingAds(false);
  };

  useEffect(() => {
    if (loanOfficerId) {
      fetchAds();
    }
  }, [loanOfficerId]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file (PNG, JPG, WebP)',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Image must be under 2MB',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `loan-officers/${loanOfficerId}/ad-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('realtor-assets')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('realtor-assets')
        .getPublicUrl(fileName);

      // Check if we're at the limit
      if (ads.length >= 3) {
        // Delete the oldest ad
        const oldestAd = ads[ads.length - 1];
        await supabase
          .from('loan_officer_ads')
          .delete()
          .eq('id', oldestAd.id);
      }

      // Insert new ad
      const isFirst = ads.length === 0;
      await supabase
        .from('loan_officer_ads')
        .insert({
          loan_officer_id: loanOfficerId,
          image_url: publicUrl,
          is_active: isFirst,
        });

      if (isFirst) {
        onAdImageChange(publicUrl);
      }

      await fetchAds();

      toast({
        title: 'Image uploaded',
        description: 'Ad image has been added.',
      });
    } catch (error) {
      console.error('Error uploading ad image:', error);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = async (adId: string) => {
    const adToRemove = ads.find(a => a.id === adId);
    if (!adToRemove) return;

    const { error } = await supabase
      .from('loan_officer_ads')
      .delete()
      .eq('id', adId);

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove ad.',
        variant: 'destructive',
      });
      return;
    }

    // If we removed the active ad, make another one active
    if (adToRemove.is_active && ads.length > 1) {
      const nextAd = ads.find(a => a.id !== adId);
      if (nextAd) {
        await handleSetActive(nextAd.id);
      }
    } else if (adToRemove.is_active) {
      onAdImageChange(null);
      await supabase
        .from('loan_officers')
        .update({ ad_image_url: null })
        .eq('id', loanOfficerId);
    }

    await fetchAds();
    toast({
      title: 'Ad removed',
      description: 'The ad image has been removed.',
    });
  };

  const handleSetActive = async (adId: string) => {
    const adToActivate = ads.find(a => a.id === adId);
    if (!adToActivate) return;

    // Deactivate all ads for this officer
    await supabase
      .from('loan_officer_ads')
      .update({ is_active: false })
      .eq('loan_officer_id', loanOfficerId);

    // Activate the selected one
    await supabase
      .from('loan_officer_ads')
      .update({ is_active: true })
      .eq('id', adId);

    // Update the legacy field
    await supabase
      .from('loan_officers')
      .update({ ad_image_url: adToActivate.image_url })
      .eq('id', loanOfficerId);

    onAdImageChange(adToActivate.image_url);
    await fetchAds();

    toast({
      title: 'Ad activated',
      description: 'This ad is now live.',
    });
  };

  const handleGenerateAI = async () => {
    if (!hasRequiredInfo) {
      toast({
        title: 'Missing required information',
        description: 'Please enter the loan officer name and NMLS number first.',
        variant: 'destructive',
      });
      return;
    }

    setGenerating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('generate-loan-officer-ad', {
        body: { loanOfficerId },
      });

      if (response.error) {
        throw response.error;
      }

      const data = response.data;

      if (data.ad_image_url) {
        await fetchAds();
        // Only update if it was the first ad
        if (ads.length === 0) {
          onAdImageChange(data.ad_image_url);
        }
        toast({
          title: 'Ad generated',
          description: 'AI-generated ad has been created.',
        });
      } else {
        throw new Error(data.error || 'No image returned');
      }
    } catch (error) {
      console.error('Error generating ad:', error);
      toast({
        title: 'Generation failed',
        description: error instanceof Error ? error.message : 'Failed to generate ad. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const isLoading = uploading || generating;
  const canAddMore = ads.length < 3;

  return (
    <div className="space-y-4">
      {!hasRequiredInfo && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Name and NMLS number are required to generate or display the ad.
          </AlertDescription>
        </Alert>
      )}

      {/* Gallery of up to 3 ads */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">Ad Images ({ads.length}/3)</h4>
          {ads.length > 0 && (
            <span className="text-xs text-muted-foreground">Click to set as active</span>
          )}
        </div>

        {loadingAds ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : ads.length === 0 ? (
          <div className="w-full max-w-md aspect-square border rounded-lg overflow-hidden bg-muted flex items-center justify-center">
            <div className="text-center p-4">
              <Image className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
              <span className="text-sm text-muted-foreground">No ad images<br />1080×1080px</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {ads.map((ad) => (
              <div
                key={ad.id}
                className={cn(
                  "relative aspect-square border-2 rounded-lg overflow-hidden cursor-pointer transition-all",
                  ad.is_active 
                    ? "border-primary ring-2 ring-primary/20" 
                    : "border-muted hover:border-primary/50"
                )}
                onClick={() => handleSetActive(ad.id)}
              >
                <img
                  src={ad.image_url}
                  alt="Loan officer ad"
                  className="w-full h-full object-cover"
                />
                {ad.is_active && (
                  <div className="absolute top-2 left-2 bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Active
                  </div>
                )}
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-7 w-7"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(ad.id);
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          className="hidden"
          id="loan-officer-ad-upload"
        />

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGenerateAI}
          disabled={isLoading || !hasRequiredInfo || !canAddMore}
        >
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate with AI
            </>
          )}
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading || !canAddMore}
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Upload Image
            </>
          )}
        </Button>

        {!canAddMore && (
          <p className="text-xs text-muted-foreground self-center">
            Maximum 3 ads. Remove one to add more.
          </p>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Recommended: 1080×1080px (1:1). The active ad will be shown on public pages.
      </p>
    </div>
  );
}
